import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type EmployeeAbsence = Tables<"employee_absences">;
export type EmployeeAbsenceInsert = TablesInsert<"employee_absences">;
export type EmployeeAbsenceUpdate = TablesUpdate<"employee_absences">;

export type EmployeeEntitlement = Tables<"employee_entitlements">;
export type EmployeeEntitlementInsert = TablesInsert<"employee_entitlements">;

type ZeroEmployeeAbsence = {
  id: string;
  personID: string;
  absenceType: EmployeeAbsence["absence_type"];
  startDate: number | string;
  endDate: number | string;
  notes?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  person?: { firstName?: string | null; lastName?: string | null } | null;
};

type ZeroEmployeeEntitlement = {
  id: string;
  personID: string;
  year: number;
  vacationDays?: number | null;
  carryOverDays?: number | null;
  notes?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? "";
}

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyAbsence(row: ZeroEmployeeAbsence): EmployeeAbsence {
  return {
    id: row.id,
    person_id: row.personID,
    absence_type: row.absenceType,
    start_date: toLegacyDate(row.startDate),
    end_date: toLegacyDate(row.endDate),
    notes: row.notes ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toLegacyAbsenceWithPerson(row: ZeroEmployeeAbsence) {
  return {
    ...toLegacyAbsence(row),
    people: row.person
      ? {
          first_name: row.person.firstName ?? null,
          last_name: row.person.lastName ?? null,
        }
      : null,
  };
}

function toLegacyEntitlement(
  row: ZeroEmployeeEntitlement,
): EmployeeEntitlement {
  return {
    id: row.id,
    person_id: row.personID,
    year: row.year,
    vacation_days: row.vacationDays ?? 0,
    carry_over_days: row.carryOverDays ?? 0,
    notes: row.notes ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

function toZeroAbsenceInput(absence: EmployeeAbsenceInsert) {
  return {
    id: absence.id ?? generateUUID(),
    personID: absence.person_id,
    absenceType: absence.absence_type,
    startDate: absence.start_date,
    endDate: absence.end_date,
    notes: absence.notes ?? null,
  };
}

function toZeroAbsenceUpdate(id: string, updates: EmployeeAbsenceUpdate) {
  return {
    id,
    ...(updates.person_id !== undefined ? { personID: updates.person_id } : {}),
    ...(updates.absence_type !== undefined
      ? { absenceType: updates.absence_type }
      : {}),
    ...(updates.start_date !== undefined
      ? { startDate: updates.start_date }
      : {}),
    ...(updates.end_date !== undefined ? { endDate: updates.end_date } : {}),
    ...(updates.notes !== undefined ? { notes: updates.notes } : {}),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function useEmployeeAbsences(
  personId: string | undefined,
  year?: number,
) {
  const query = personId
    ? appQueries.employeeAbsences.byPerson({ personID: personId, year })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!personId });
  const absences = ((data ?? []) as ZeroEmployeeAbsence[]).map(toLegacyAbsence);

  return {
    data: absences,
    isLoading:
      !!personId && details.type !== "complete" && absences.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useAllEmployeeAbsences(year?: number) {
  const [data, details] = useQuery(appQueries.employeeAbsences.all({ year }));
  const absences = ((data ?? []) as ZeroEmployeeAbsence[]).map(
    toLegacyAbsenceWithPerson,
  );

  return {
    data: absences,
    isLoading: details.type !== "complete" && absences.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateEmployeeAbsence() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (absence: EmployeeAbsenceInsert) => {
    const input = toZeroAbsenceInput(absence);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.employeeAbsences.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Absence create was rejected",
      );
      return toLegacyAbsence(input);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      absence: EmployeeAbsenceInsert,
      options?: MutationOptions<EmployeeAbsence>,
    ) => {
      void run(absence)
        .then((data) => {
          toast.success("Absence added");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to add absence: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateEmployeeAbsence() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: EmployeeAbsenceUpdate;
  }) => {
    const input = toZeroAbsenceUpdate(id, updates);
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.employeeAbsences.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Absence update was rejected",
      );
      return toLegacyAbsence({
        id,
        personID: input.personID ?? "",
        absenceType: input.absenceType ?? "other",
        startDate: input.startDate ?? "",
        endDate: input.endDate ?? "",
        notes: input.notes ?? null,
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: EmployeeAbsenceUpdate },
      options?: MutationOptions<EmployeeAbsence>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Absence updated");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to update absence: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteEmployeeAbsence() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.employeeAbsences.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Absence delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Absence deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete absence: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useEmployeeEntitlements(personId: string | undefined) {
  const query = personId
    ? appQueries.employeeEntitlements.byPerson({ personID: personId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!personId });
  const entitlements = ((data ?? []) as ZeroEmployeeEntitlement[]).map(
    toLegacyEntitlement,
  );

  return {
    data: entitlements,
    isLoading:
      !!personId && details.type !== "complete" && entitlements.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useUpsertEntitlement() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (entitlement: EmployeeEntitlementInsert) => {
    const input = {
      id: entitlement.id ?? generateUUID(),
      personID: entitlement.person_id,
      year: entitlement.year,
      vacationDays: entitlement.vacation_days ?? 0,
      carryOverDays: entitlement.carry_over_days ?? 0,
      notes: entitlement.notes ?? null,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(
        appMutators.employeeEntitlements.upsert(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Entitlement save was rejected",
      );
      return toLegacyEntitlement(input);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      entitlement: EmployeeEntitlementInsert,
      options?: MutationOptions<EmployeeEntitlement>,
    ) => {
      void run(entitlement)
        .then((data) => {
          toast.success("Entitlement saved");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to save entitlement: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
